import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useNotifications } from "../../components/Notification";
import DeactivationRequestDialog from "./DeactivateAccount";

export default function Account() {
  const { data, loading, error } = useQuery<{ salesPartner: SalesPartner }>(
    GET_SALES_PARTNER
  );
  if (loading) return <LinearProgress color="error" />;
  if (error) return <>ERROR</>;

  const salesPartner = data ? data.salesPartner : null;
  if (!salesPartner) return <>404</>;

  return <Form salesPartner={salesPartner} />;
}

function Form(props: { salesPartner: SalesPartner }) {
  const { showNotification } = useNotifications();
  const salesPartner = props.salesPartner;

  const [email, setEmail] = useState(salesPartner.email);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordAgain, setNewPasswordAgain] = useState("");
  const [openDeactivationDialog, setOpenDeactivationDialog] = useState(false);
  const [savingAction, setSavingAction] = useState<
    "save" | "deactivate" | null
  >(null);
  const [editSalesPartner] = useMutation(EDIT_SALES_PARTNER);

  return (
    <>
      <Card sx={{ width: { xs: "100%", md: "50%" } }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField label="Name" value={salesPartner.name} disabled />
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <hr />
            <b>Change Password</b>
            <span style={{ fontSize: "12px" }}>
              If you want to change your password, enter a new password here.
              <br />
              Keep empty if you don't want to change your password
            </span>
            <TextField
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              type="password"
              label="New Password Again"
              value={newPasswordAgain}
              onChange={(e) => setNewPasswordAgain(e.target.value)}
              helperText={
                newPassword !== newPasswordAgain && (
                  <span style={{ color: "red" }}>
                    new passwords don't match
                  </span>
                )
              }
            />
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between" }}>
          <Button
            style={{ background: "#f50057" }}
            disabled={savingAction !== null}
            variant="contained"
            onClick={() => setOpenDeactivationDialog(true)}
          >
            {savingAction === "deactivate" ? (
              <CircularProgress size={24} color={"inherit"} />
            ) : (
              "Request Account Deactivation"
            )}
          </Button>
          <Button
            disabled={!canSave() || savingAction !== null}
            variant="contained"
            onClick={handleSave}
          >
            {savingAction === "save" ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </CardActions>
      </Card>
      <DeactivationRequestDialog
        open={openDeactivationDialog}
        setOpen={setOpenDeactivationDialog}
        onConfirm={handleRequestDeactivation}
      />
    </>
  );

  function canSave() {
    if (email !== salesPartner.email) return true;
    if (
      currentPassword &&
      newPassword &&
      newPasswordAgain &&
      newPassword === newPasswordAgain
    )
      return true;

    return false;
  }

  function handleSave() {
    setSavingAction("save");
    editSalesPartner({
      variables: { id: salesPartner.id, email, currentPassword, newPassword },
      refetchQueries: ["GetSalesPartner"],
    }).then(
      () => {
        setSavingAction(null);
        setCurrentPassword("");
        setNewPassword("");
        setNewPasswordAgain("");
        showNotification({
          severity: "success",
          message: "Account was updated",
        });
      },
      () => {
        setSavingAction(null);
        showNotification({
          severity: "error",
          message: "Failed to update account",
        });
      }
    );
  }

  function handleRequestDeactivation() {
    setSavingAction("deactivate");
    editSalesPartner({
      variables: { id: salesPartner.id, requestDeactivation: true },
    }).then(
      () => {
        setSavingAction(null);
        showNotification({
          severity: "success",
          message: "Account deactivation request has been sent",
        });
      },
      () => {
        setSavingAction(null);
        showNotification({
          severity: "error",
          message: "Failed to send deactivation request",
        });
      }
    );
  }
}

const GET_SALES_PARTNER = gql`
  query GetSalesPartner {
    salesPartner {
      id
      name
      email
    }
  }
`;

type SalesPartner = {
  id: string;
  name: string;
  email: string;
};

const EDIT_SALES_PARTNER = gql`
  mutation EditSalesPartner(
    $id: ID!
    $email: String
    $currentPassword: String
    $newPassword: String
    $requestDeactivation: Boolean
  ) {
    editSalesPartner(
      input: {
        id: $id
        email: $email
        currentPassword: $currentPassword
        newPassword: $newPassword
        requestDeactivation: $requestDeactivation
      }
    )
  }
`;
